
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

// ✅ MUI
import {
  Box,
  Stack,
  Typography,
  Dialog,
  Fade,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LockIcon from "@mui/icons-material/Lock";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useSocket } from "../socket/SocketContext";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const allowEmptyPassword = String(process.env.REACT_APP_COMMAND_ALLOW_EMPTY_PASSWORD || "false").toLowerCase() === "true";
const SHOW_PASSWORD_SECTION = true;

// -------------------------------------
// Helpers
// -------------------------------------
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  // tumhare backend me kabhi x-auth-token use hua hai, kabhi Authorization
  // safer: dono bhej do
  return {
    "x-auth-token": token,
    Authorization: token ? `Bearer ${token}` : undefined,
  };
};

const safeString = (v) => (v === null || v === undefined ? "" : String(v));
const DRAFT_PREFIX = "cmd_modal_draft_v1";
const LAST_SELECTION_PREFIX = "cmd_modal_last_selection_v1";
const normalizeIdentity = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    if (value.$oid) return String(value.$oid);
    if (value._id) return String(value._id);
    return "";
  }
  return String(value);
};

const PROTOCOL_HINTS = [
  "g17s",
  "g17h",
  "gt06",
  "concox",
  "pt20",
  "ps25",
  "v5",
  "xyz1",
  "g108",
  "jt808",
  "bsj",
];

const resolveDeviceProtocolHint = (device) => {
  const fields = [
    device?.protocol,
    device?.protocolName,
    device?.deviceType,
    device?.model,
    device?.lastProtocolUsed,
    device?.name,
    device?.vehicleName,
  ]
    .map((value) => safeString(value).trim().toLowerCase())
    .filter(Boolean);

  for (const field of fields) {
    for (const hint of PROTOCOL_HINTS) {
      if (field.includes(hint)) {
        return hint.toUpperCase();
      }
    }
    if (field.includes("g17")) {
      return "GT06";
    }
  }

  return "";
};

const guessProtocolLabel = (device) => {
  const inferredProtocol =
    resolveDeviceProtocolHint(device) ||
    safeString(device?.deviceType || device?.protocol || device?.protocolName || "").toUpperCase() ||
    "GT06";
  const model = device?.model ? ` • Model: ${device.model}` : "";
  const lastProto = device?.lastProtocolUsed ? ` • Last Protocol: ${device.lastProtocolUsed}` : "";
  return `${inferredProtocol}${model}${lastProto}`;
};

const getDraftKey = (deviceIdentity, commandType) => {
  if (!deviceIdentity || !commandType) return null;
  return `${DRAFT_PREFIX}:${deviceIdentity}:${commandType}`;
};

const getLastSelectionKey = (deviceIdentity) => {
  if (!deviceIdentity) return null;
  return `${LAST_SELECTION_PREFIX}:${deviceIdentity}`;
};

const readDraft = (deviceIdentity, commandType) => {
  const key = getDraftKey(deviceIdentity, commandType);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeDraft = (deviceIdentity, commandType, draft) => {
  const key = getDraftKey(deviceIdentity, commandType);
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {}
};

const readLastSelection = (deviceIdentity) => {
  const key = getLastSelectionKey(deviceIdentity);
  if (!key) return "";
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeLastSelection = (deviceIdentity, commandType) => {
  const key = getLastSelectionKey(deviceIdentity);
  if (!key) return;
  try {
    sessionStorage.setItem(key, safeString(commandType));
  } catch {}
};

const isRawTemplate = (tpl) => {
  // agar tum raw template DB me add karoge: type = "custom_raw" ya baseCommand empty etc
  // UI: "Raw" tab alag dikhayenge anyway
  return tpl?.type === "custom_raw" || tpl?.isRaw === true;
};

const isPasswordParamDef = (p) => {
  const fieldType = safeString(p?.type).trim().toLowerCase();
  const fieldName = safeString(p?.name).trim().toLowerCase().replace(/\s+/g, "");
  if (fieldType === "password") return true;
  return ["password", "pwd", "passcode", "devicepassword"].includes(fieldName);
};

const pickFirstTemplate = (templates, preferredTypes) => {
  if (!Array.isArray(templates) || templates.length === 0) return null;
  const byType = new Map(templates.map((t) => [safeString(t?.type).toLowerCase(), t]));
  for (const type of preferredTypes) {
    const found = byType.get(String(type).toLowerCase());
    if (found) return found;
  }
  return null;
};

const buildDynamicTemplatesForProtocol = (device, options = {}) => {
  const rawProto = [
    safeString(device?.protocol),
    safeString(device?.protocolName),
    safeString(device?.deviceType),
    safeString(device?.model),
    safeString(device?.lastProtocolUsed),
    safeString(device?.name),
    safeString(device?.vehicleName),
  ]
    .join(" ")
    .toLowerCase();
  const forceRelay = options?.commandScope === "engine_only";
  const supportsRelay =
    forceRelay ||
    rawProto.includes("g17") ||
    rawProto.includes("g17h") ||
    rawProto.includes("gt06") ||
    rawProto.includes("concox") ||
    rawProto.includes("pt20") ||
    rawProto.includes("v5") ||
    rawProto.includes("ps25") ||
    rawProto.includes("xyz1") ||
    rawProto.includes("g108") ||
    rawProto.includes("g17s");
  const dynamic = [];

  if (supportsRelay) {
    dynamic.push(
      {
        type: "power_on",
        name: "Engine ON",
        category: "Engine & Power",
        dangerous: false,
        confirmationRequired: true,
        requiresPassword: false,
        parameters: [],
        dynamic: true,
      },
      {
        type: "power_off",
        name: "Engine OFF",
        category: "Engine & Power",
        dangerous: true,
        confirmationRequired: true,
        requiresPassword: false,
        parameters: [],
        dynamic: true,
      }
    );
  }

  const supportsAudioMonitor =
    rawProto.includes("jt808") ||
    rawProto.includes("bsj") ||
    !!safeString(device?.msdn).trim();

  if (supportsAudioMonitor) {
    dynamic.push(
      {
        type: "audio_monitor",
        name: "Call / Listen",
        category: "Audio Call",
        description: "Start JT808 audio monitor from dashboard.",
        dangerous: false,
        confirmationRequired: false,
        requiresPassword: false,
        parameters: [
          {
            name: "channel",
            type: "number",
            required: false,
            min: 1,
            max: 32,
            description: "Audio monitor channel",
            example: 1,
            order: 1,
          },
          {
            name: "streamType",
            type: "number",
            required: false,
            min: 0,
            max: 1,
            description: "0 = main stream, 1 = sub stream",
            example: 1,
            order: 2,
          },
        ],
        dynamic: true,
      },
      {
        type: "stop_audio_monitor",
        name: "Stop Call",
        category: "Audio Call",
        description: "Stop JT808 audio monitor for the selected device.",
        dangerous: false,
        confirmationRequired: false,
        requiresPassword: false,
        parameters: [
          {
            name: "channel",
            type: "number",
            required: false,
            min: 1,
            max: 32,
            description: "Audio monitor channel",
            example: 1,
            order: 1,
          },
        ],
        dynamic: true,
      }
    );
  }

  return dynamic;
};

const statusColor = (status) => {
  switch (status) {
    case "sending":
      return "info";
    case "pending":
    case "sent":
      return "warning";
    case "queued":
      return "info";
    case "executed":
    case "delivered":
      return "success";
    case "failed":
    case "timeout":
      return "error";
    default:
      return "default";
  }
};

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const ENGINE_OFF_TYPES = ["power_off", "engine_off", "engine_stop", "relay_off"];
const ENGINE_ON_TYPES = ["power_on", "engine_on", "engine_start", "relay_on"];

const SIMPLE_LABEL_OVERRIDES = {
  custom_raw: "Custom",
  power_off: "EngineStop",
  engine_off: "EngineStop",
  engine_stop: "EngineStop",
  relay_off: "EngineStop",
  power_on: "EngineResume",
  engine_on: "EngineResume",
  engine_start: "EngineResume",
  relay_on: "EngineResume",
};

const toPascalCase = (value = "") =>
  safeString(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("");

const getSimpleCommandLabel = (template = {}) => {
  const type = safeString(template?.type).toLowerCase();
  if (SIMPLE_LABEL_OVERRIDES[type]) return SIMPLE_LABEL_OVERRIDES[type];
  return toPascalCase(template?.type || template?.name || "");
};

// -------------------------------------
// Component
// -------------------------------------
const SendCommandModal = ({
  device,
  onClose,
  onCommandSent,
  assetsData,
  initialCommandType = "",
  uiMode = "advanced",
  commandScope = "full",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSimpleMode = uiMode === "simple";
  const [stableDevice] = useState(() => device);

  // Backend-driven templates
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [hydratedDevice, setHydratedDevice] = useState(null);
  const [simpleCommandPickerOpen, setSimpleCommandPickerOpen] = useState(false);

  // Selection flow
  const [selectedTpl, setSelectedTpl] = useState(null); // full template object from backend
  const [step, setStep] = useState("select"); // select -> params -> confirm -> review -> transport
  const [parameters, setParameters] = useState({});
  const [devicePassword, setDevicePassword] = useState("");
  const [priority, setPriority] = useState("normal");
  const [confirmText, setConfirmText] = useState("");
  // Status UI
  const [isSending, setIsSending] = useState(false);
  const [commandStatus, setCommandStatus] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [activeCommandId, setActiveCommandId] = useState("");
  const shouldShowPasswordField =
    SHOW_PASSWORD_SECTION && !!selectedTpl?.requiresPassword && !isSimpleMode;

  // Socket
  const modalDevice = useMemo(() => {
    if (!hydratedDevice) return stableDevice;
    return {
      ...stableDevice,
      ...hydratedDevice,
      liveData: hydratedDevice?.liveData || stableDevice?.liveData,
    };
  }, [hydratedDevice, stableDevice]);
  const deviceIdentity = normalizeIdentity(modalDevice?._id || modalDevice?.id || modalDevice?.imei);
  const selectedTypeRef = useRef("");
  const simpleModeAutoSelectKeyRef = useRef("");
  const commandCloseTimerRef = useRef(null);
  const commandPollTimerRef = useRef(null);
  const { on: onSocketEvent, joinDevice, leaveDevice, subscribeToDevice, unsubscribeFromDevice } = useSocket();

  useEffect(() => {
    let active = true;

    const hydrateDevice = async () => {
      const deviceId = normalizeIdentity(stableDevice?._id || stableDevice?.id);
      const hasEnoughMetadata =
        !!safeString(
          stableDevice?.protocol ||
            stableDevice?.protocolName ||
            stableDevice?.deviceType ||
            stableDevice?.model
        ).trim();

      setHydratedDevice(null);

      if (!deviceId || hasEnoughMetadata || !/^[a-f0-9]{24}$/i.test(deviceId)) {
        return;
      }

      try {
        const res = await axios.get(`${backendUrl}/devices/${deviceId}`, {
          headers: getAuthHeaders(),
        });
        const nextDevice = res?.data?.device || res?.data?.data || res?.data;
        if (active && nextDevice && typeof nextDevice === "object") {
          setHydratedDevice(nextDevice);
        }
      } catch {
        // Keep modal functional with the partial device object if hydration fails.
      }
    };

    hydrateDevice();
    return () => {
      active = false;
    };
  }, [
    stableDevice?._id,
    stableDevice?.id,
    stableDevice?.protocol,
    stableDevice?.protocolName,
    stableDevice?.deviceType,
    stableDevice?.model,
  ]);

  useEffect(() => {
    const normalizedDeviceId = String(stableDevice?._id || stableDevice?.id || "").trim();
    const normalizedImei = String(stableDevice?.imei || "").trim();

    if (normalizedDeviceId) {
      joinDevice?.(normalizedDeviceId);
    }
    if (normalizedImei) {
      subscribeToDevice?.(normalizedImei);
    }

    return () => {
      if (commandCloseTimerRef.current) {
        clearTimeout(commandCloseTimerRef.current);
      }
      if (normalizedDeviceId) {
        leaveDevice?.(normalizedDeviceId);
      }
      if (normalizedImei) {
        unsubscribeFromDevice?.(normalizedImei);
      }
    };
  }, [
    joinDevice,
    leaveDevice,
    subscribeToDevice,
    unsubscribeFromDevice,
    stableDevice?._id,
    stableDevice?.id,
    stableDevice?.imei,
  ]);

  // Join/listen for device events
  useEffect(() => {
    if (!deviceIdentity) return;
    const modalDeviceId = normalizeIdentity(modalDevice?._id || modalDevice?.id);
    const modalImei = safeString(modalDevice?.imei).trim();
    const matchesCommandEvent = (data = {}) => {
      const eventDeviceId = normalizeIdentity(data.deviceId);
      const eventImei = safeString(data.imei).trim();
      if (modalDeviceId && eventDeviceId && modalDeviceId === eventDeviceId) return true;
      if (modalImei && eventImei && modalImei === eventImei) return true;
      return false;
    };

    const handleRealtimeEvent = (data = {}, fallbackStatus = "") => {
      if (!matchesCommandEvent(data)) return;
      const nextStatus = safeString(data.status || fallbackStatus).toLowerCase();
      const responseText = safeString(data.response).trim();

      if (nextStatus === "failed" || nextStatus === "timeout") {
        setErrorMsg(responseText || data.error || "Command failed.");
        setSuccessMsg(null);
        setCommandStatus("failed");
        setIsSending(false);
        return;
      }

      if (nextStatus === "queued") {
        setSuccessMsg(responseText || "Command queued. Device will receive it when it reconnects.");
        setErrorMsg(null);
        setCommandStatus("queued");
        setIsSending(false);
        return;
      }

      if (nextStatus === "delivered" || nextStatus === "executed" || nextStatus === "success") {
        setSuccessMsg(responseText || "Command executed successfully.");
        setErrorMsg(null);
        setCommandStatus(nextStatus || "delivered");
        setIsSending(false);

        if (commandCloseTimerRef.current) {
          clearTimeout(commandCloseTimerRef.current);
        }
        commandCloseTimerRef.current = setTimeout(() => onClose?.(), 2200);
      }

      onCommandSent?.({
        commandId: data.commandId,
        commandType: data.commandType,
        status: data.status || fallbackStatus,
        response: data.response,
        timestamp: data.timestamp,
      });
    };

    const onSuccess = (data) => {
      handleRealtimeEvent(data, "delivered");
    };

    const onStatus = (data) => {
      handleRealtimeEvent(data, safeString(data.status).toLowerCase());
    };

    const onResponse = (data) => {
      if (!matchesCommandEvent(data)) return;
      const responseText = safeString(data.response).trim();
      if (!responseText) return;

      setSuccessMsg(responseText);
      setErrorMsg(null);
      setCommandStatus("delivered");
      setIsSending(false);

      if (commandCloseTimerRef.current) clearTimeout(commandCloseTimerRef.current);
      commandCloseTimerRef.current = setTimeout(() => onClose?.(), 2200);
    };

    const onFailed = (data) => {
      if (!matchesCommandEvent(data)) return;
      setErrorMsg(safeString(data.response || data.error).trim() || "Command failed.");
      setSuccessMsg(null);
      setCommandStatus("failed");
      setIsSending(false);
    };

    const onQueued = (data) => {
      handleRealtimeEvent(data, "queued");
    };

    const offSuccess = onSocketEvent?.("command:success", onSuccess);
    const offFailed = onSocketEvent?.("command:failed", onFailed);
    const offQueued = onSocketEvent?.("command:queued", onQueued);
    const offStatus = onSocketEvent?.("command:status", onStatus);
    const offResponse = onSocketEvent?.("command:response", onResponse);

    return () => {
      offSuccess?.();
      offFailed?.();
      offQueued?.();
      offStatus?.();
      offResponse?.();
    };
  }, [onSocketEvent, deviceIdentity, modalDevice?._id, modalDevice?.id, modalDevice?.imei, onCommandSent, onClose]);

  useEffect(() => {
    if (!activeCommandId) return undefined;

    const terminalStatuses = new Set(["delivered", "executed", "failed", "timeout"]);
    if (terminalStatuses.has(safeString(commandStatus).toLowerCase())) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const pollCommandResult = async () => {
      if (cancelled) return;
      attempts += 1;

      try {
        const res = await axios.get(`${backendUrl}/commands/${activeCommandId}`, {
          headers: getAuthHeaders(),
        });

        const command = res?.data?.data || {};
        const nextStatus = safeString(command.status).toLowerCase();
        const responseText = safeString(command.response).trim();

        if (responseText && ["delivered", "executed", "failed", "timeout"].includes(nextStatus)) {
          setSuccessMsg(nextStatus === "failed" || nextStatus === "timeout" ? null : responseText);
          setErrorMsg(nextStatus === "failed" || nextStatus === "timeout" ? responseText || "Command failed." : null);
          setCommandStatus(nextStatus);
          setIsSending(false);

          if (nextStatus === "delivered" || nextStatus === "executed") {
            if (commandCloseTimerRef.current) clearTimeout(commandCloseTimerRef.current);
            commandCloseTimerRef.current = setTimeout(() => onClose?.(), 2200);
          }
          return;
        }
      } catch {
        // Keep socket-first flow; polling is only a silent fallback.
      }

      if (!cancelled && attempts < 8) {
        commandPollTimerRef.current = setTimeout(pollCommandResult, 1000);
      }
    };

    commandPollTimerRef.current = setTimeout(pollCommandResult, 900);

    return () => {
      cancelled = true;
      if (commandPollTimerRef.current) {
        clearTimeout(commandPollTimerRef.current);
      }
    };
  }, [activeCommandId, commandStatus, onClose]);

  // ------------------------
  // Fetch templates from backend
  // ------------------------
  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);

    try {
      const protocolHint = resolveDeviceProtocolHint(modalDevice);
      const res = await axios.get(`${backendUrl}/gps-commands/templates`, {
        params: {
          protocol: protocolHint || "",
          deviceType: modalDevice?.deviceType || "",
        },
        headers: getAuthHeaders(),
      });

      const list = res?.data?.data || [];
      setTemplates(list);
    } catch (e) {
      setTemplatesError(
        e?.response?.data?.message || e?.message || "Failed to load command templates"
      );
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (!deviceIdentity) return;
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deviceIdentity,
    modalDevice?.protocol,
    modalDevice?.protocolName,
    modalDevice?.deviceType,
    modalDevice?.model,
    modalDevice?.lastProtocolUsed,
  ]);

  // ------------------------
  // Derived UI: categories grouped
  // ------------------------
  const categories = useMemo(() => {
    const dynamicTemplates = buildDynamicTemplatesForProtocol(modalDevice, {
      commandScope,
    });
    const byType = new Map();
    for (const t of templates) {
      byType.set(safeString(t?.type).toLowerCase(), t);
    }
    for (const t of dynamicTemplates) {
      const key = safeString(t?.type).toLowerCase();
      if (!byType.has(key)) byType.set(key, t);
    }

    const map = new Map();
    for (const t of byType.values()) {
      const cat = t.category || "Other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(t);
    }

    // sort by category name + command name
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([cat, items]) => ({
        category: cat,
        commands: items.sort((x, y) => safeString(x.name).localeCompare(safeString(y.name))),
      }));
  }, [templates, modalDevice, commandScope]);

  const availableTemplates = useMemo(() => categories.flatMap((c) => c.commands), [categories]);

  const engineOffTemplate = useMemo(
    () =>
      pickFirstTemplate(availableTemplates, [
        "power_off",
        "engine_off",
        "engine_stop",
        "relay_off",
        "gt06_engine_stop",
        "concox_engine_stop",
      ]),
    [availableTemplates]
  );
  const engineOnTemplate = useMemo(
    () =>
      pickFirstTemplate(availableTemplates, [
        "power_on",
        "engine_on",
        "engine_start",
        "relay_on",
        "gt06_engine_start",
        "tk103_engine_start",
      ]),
    [availableTemplates]
  );

  // ------------------------
  // Reset when select changes / open
  // ------------------------
  const resetFlow = () => {
    if (commandCloseTimerRef.current) clearTimeout(commandCloseTimerRef.current);
    if (commandPollTimerRef.current) clearTimeout(commandPollTimerRef.current);
    setSelectedTpl(null);
    setStep("select");
    setParameters({});
    setDevicePassword("");
    setPriority("normal");
    setConfirmText("");
    setCommandStatus(null);
    setSuccessMsg(null);
    setErrorMsg(null);
    setIsSending(false);
    setActiveCommandId("");
  };

  // NOTE:
  // Do not reset flow on every device live update while modal is open.
  // Some screens pass a frequently refreshed `device` object; resetting here
  // clears password/params after each keypress-like re-render.

  // ------------------------
  // Select template
  // ------------------------
  const selectTemplate = (tpl, { advance = true } = {}) => {
    const draft = readDraft(deviceIdentity, tpl?.type);
    selectedTypeRef.current = tpl?.type || "";
    writeLastSelection(deviceIdentity, tpl?.type);
    setSelectedTpl(tpl);
    setParameters(draft?.parameters || {});
    setDevicePassword(draft?.devicePassword || "");
    setConfirmText("");
    setSuccessMsg(null);
    setErrorMsg(null);
    setCommandStatus(null);

    // step decision
    const hasParams = Array.isArray(tpl?.parameters) && tpl.parameters.length > 0;
    const needsPwd = !!tpl?.requiresPassword;

    if (!advance) return;

    if (hasParams || needsPwd) setStep("params");
    else if (tpl?.confirmationRequired || tpl?.dangerous) setStep("confirm");
    else setStep("review");
  };

  const onSelectTemplate = (tpl) => selectTemplate(tpl, { advance: true });

  useEffect(() => {
    if (!deviceIdentity || selectedTpl?.type) return;
    if (!Array.isArray(availableTemplates) || availableTemplates.length === 0) return;

    const rememberedType =
      safeString(initialCommandType) ||
      selectedTypeRef.current ||
      readLastSelection(deviceIdentity);
    if (!rememberedType) return;

    const rememberedTemplate = availableTemplates.find((t) => t?.type === rememberedType);
    if (rememberedTemplate) {
      onSelectTemplate(rememberedTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTemplates, deviceIdentity, selectedTpl?.type, initialCommandType]);

  const simpleTemplates = useMemo(() => {
    if (!isSimpleMode) return [];

    if (commandScope === "engine_only") {
      const offTemplate = pickFirstTemplate(availableTemplates, ENGINE_OFF_TYPES);
      const onTemplate = pickFirstTemplate(availableTemplates, ENGINE_ON_TYPES);
      return [offTemplate, onTemplate].filter(Boolean);
    }

    const seen = new Set();
    return availableTemplates
      .filter((tpl) => {
        const type = safeString(tpl?.type).toLowerCase();
        if (!type || seen.has(type)) return false;
        seen.add(type);
        return true;
      })
      .sort((a, b) => {
        const aType = safeString(a?.type).toLowerCase();
        const bType = safeString(b?.type).toLowerCase();
        const rank = (type) => {
          if (ENGINE_OFF_TYPES.includes(type)) return 0;
          if (ENGINE_ON_TYPES.includes(type)) return 1;
          if (type === "custom_raw") return 2;
          return 10;
        };
        const rankDiff = rank(aType) - rank(bType);
        if (rankDiff !== 0) return rankDiff;
        return getSimpleCommandLabel(a).localeCompare(getSimpleCommandLabel(b));
      });
  }, [availableTemplates, commandScope, isSimpleMode]);

  const selectedSimpleTemplate = useMemo(() => {
    const selectedType = safeString(selectedTpl?.type).toLowerCase();
    if (!selectedType) return null;
    return simpleTemplates.find((tpl) => safeString(tpl?.type).toLowerCase() === selectedType) || null;
  }, [simpleTemplates, selectedTpl?.type]);

useEffect(() => {
  if (!isSimpleMode) return;
  if (simpleCommandPickerOpen) return;
  if (!simpleTemplates.length) {
    setSelectedTpl(null);
    simpleModeAutoSelectKeyRef.current = "";
    return;
  }

  const preferredTypes = [
    safeString(initialCommandType).toLowerCase(),
    safeString(selectedTpl?.type).toLowerCase(),
    safeString(readLastSelection(deviceIdentity)).toLowerCase(),
  ].filter(Boolean);

  const nextTemplate =
    preferredTypes
      .map((type) => simpleTemplates.find((tpl) => safeString(tpl?.type).toLowerCase() === type))
      .find(Boolean) || simpleTemplates[0];

  const nextType = safeString(nextTemplate?.type).toLowerCase();
  const currentType = safeString(selectedTpl?.type).toLowerCase();
  const autoSelectKey = `${deviceIdentity}:${nextType}:${safeString(initialCommandType).toLowerCase()}`;

  // ✅ Only update if key changes (prevent infinite loops)
  if (
    nextTemplate &&
    currentType !== nextType &&
    simpleModeAutoSelectKeyRef.current !== autoSelectKey
  ) {
    simpleModeAutoSelectKeyRef.current = autoSelectKey;
    selectTemplate(nextTemplate, { advance: false });
  }
}, [isSimpleMode, simpleTemplates, initialCommandType, deviceIdentity, selectedTpl?.type, simpleCommandPickerOpen]);

  const renderedSimpleTemplates = simpleTemplates;
  const simpleCommandMenuOpen = Boolean(simpleCommandPickerOpen);


  useEffect(() => {
    if (!selectedTpl?.type) return;
    writeDraft(deviceIdentity, selectedTpl.type, { parameters, devicePassword });
  }, [deviceIdentity, selectedTpl?.type, parameters, devicePassword]);

  // ------------------------
  // Validation
  // ------------------------
  const validateParams = () => {
    if (!selectedTpl) return { ok: true };

    if (shouldShowPasswordField && !allowEmptyPassword) {
      if (!devicePassword || devicePassword.trim().length < 4) {
        return { ok: false, msg: "Login password required (min 4 chars)" };
      }
    }

    const defs = Array.isArray(selectedTpl.parameters) ? selectedTpl.parameters : [];
    for (const p of defs) {
      if (!SHOW_PASSWORD_SECTION && isPasswordParamDef(p)) continue;

      const val = parameters?.[p.name];

      if (p.required && (val === undefined || val === null || String(val).trim() === "")) {
        return { ok: false, msg: `Missing parameter: ${p.name}` };
      }

      if (p.type === "number" && val !== undefined && val !== null && String(val).trim() !== "") {
        const num = Number(val);
        if (Number.isNaN(num)) return { ok: false, msg: `${p.name} must be a number` };
        if (typeof p.min === "number" && num < p.min)
          return { ok: false, msg: `${p.name} must be ≥ ${p.min}` };
        if (typeof p.max === "number" && num > p.max)
          return { ok: false, msg: `${p.name} must be ≤ ${p.max}` };
      }
    }

    return { ok: true };
  };

  const validateConfirm = () => {
    if (!selectedTpl) return false;
    if (!(selectedTpl.confirmationRequired || selectedTpl.dangerous)) return true;
    return confirmText.trim().toUpperCase() === "CONFIRM";
  };

  // ------------------------
  // Send commands
  // ------------------------
  const sendTypedCommand = async () => {
    if (!modalDevice?._id || !selectedTpl?.type) return;

    const v = validateParams();
    if (!v.ok) {
      setErrorMsg(v.msg);
      return;
    }

    setErrorMsg(null);
    setSuccessMsg(null);
    setIsSending(true);
    setCommandStatus("sending");

    try {
      const payloadParams = { ...parameters };
      if (!SHOW_PASSWORD_SECTION) {
        const defs = Array.isArray(selectedTpl?.parameters) ? selectedTpl.parameters : [];
        defs.forEach((p) => {
          if (isPasswordParamDef(p)) {
            delete payloadParams[p?.name];
          }
        });
        delete payloadParams.password;
        delete payloadParams.pwd;
        delete payloadParams.passcode;
        delete payloadParams.devicePassword;
        delete payloadParams.devicepassword;
      }

      const payload = {
        deviceId: modalDevice._id,
        commandType: selectedTpl.type,
        parameters: payloadParams,
        priority,
        expectResponse: true,
        maxRetries: 3,
      };

      if (shouldShowPasswordField) {
        payload.parameters.password = devicePassword.trim();
      }

      const res = await axios.post(`${backendUrl}/gps-commands/send-by-type`, payload, {
        headers: getAuthHeaders(),
      });

      if (res?.data?.success) {
        const acceptedCommandId = safeString(res?.data?.data?.commandId).trim();
        setActiveCommandId(acceptedCommandId);
        const nextStatus = safeString(res?.data?.data?.status || res?.data?.status || "pending").toLowerCase();
        setSuccessMsg(
          nextStatus === "executed"
            ? safeString(res?.data?.data?.result?.action || "Command executed successfully.")
            : "Waiting for device response..."
        );
        setCommandStatus(nextStatus === "accepted" || nextStatus === "pending" ? "sent" : nextStatus);
        if (nextStatus === "executed") {
          setIsSending(false);
          onCommandSent?.({
            commandId: res?.data?.data?.commandId,
            commandType: selectedTpl.type,
            status: "executed",
            response: res?.data?.data?.result?.action || "executed",
            timestamp: new Date().toISOString(),
          });
          setTimeout(() => onClose?.(), 1800);
        }
        // socket will update later
      } else {
        setErrorMsg(res?.data?.message || "Failed to send command");
        setIsSending(false);
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.response?.data?.error || e?.message);
      setIsSending(false);
    }
  };

  const sendRawCommand = async (rawCommand) => {
    if (!modalDevice?._id) return;

    if (!rawCommand || !rawCommand.trim()) {
      setErrorMsg("Raw command is required");
      return;
    }

    setErrorMsg(null);
    setSuccessMsg(null);
    setIsSending(true);
    setCommandStatus("sending");

    try {
      const payload = {
        deviceId: modalDevice._id,
        rawCommand: rawCommand.trim(),
        commandName: "Raw Command",
        priority,
        expectResponse: true,
        maxRetries: 3,
        timeout: 30000,
      };

      const res = await axios.post(`${backendUrl}/gps-commands/send-raw`, payload, {
        headers: getAuthHeaders(),
      });

      if (res?.data?.success) {
        const acceptedCommandId = safeString(res?.data?.data?.commandId).trim();
        setActiveCommandId(acceptedCommandId);
        setSuccessMsg("Waiting for device response...");
        setCommandStatus(safeString(res?.data?.data?.status || "sent").toLowerCase());
      } else {
        setErrorMsg(res?.data?.message || "Failed to send raw command");
        setIsSending(false);
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.response?.data?.error || e?.message);
      setIsSending(false);
    }
  };

  // SMS helper
  const getDevicePhoneNumber = () => {
    if (!modalDevice?.imei) return null;
    if (!Array.isArray(assetsData) || assetsData.length === 0) return null;
    const asset = assetsData.find((a) => String(a.IMEI) === String(modalDevice.imei));
    return asset?.simCard?.MSDN || asset?.MSDN || null;
  };

  const prepareSms = (text) => {
    const phone = getDevicePhoneNumber();
    if (!phone) {
      setErrorMsg("Device phone number (MSDN) not available for SMS.");
      return;
    }
    const smsUrl = `sms:${phone}?body=${encodeURIComponent(text)}`;
    window.location.href = smsUrl;

    setSuccessMsg(`💬 SMS prepared for ${phone}. Send it from your SMS app.`);
    setTimeout(() => onClose?.(), 1600);
  };

  // Review content: NOTE: backend builds final command. UI just shows params.
  const protocolLabel = useMemo(() => guessProtocolLabel(modalDevice), [modalDevice]);

  const hasParams = !!(selectedTpl?.parameters?.length);
  const needsConfirm = !!(selectedTpl?.confirmationRequired || selectedTpl?.dangerous);

  // For Raw UI (no hardcode)
  const rawCommandValue = safeString(parameters.rawCommand);

  // ------------------------
  // UI Blocks
  // ------------------------
  const Header = () => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack spacing={0.3} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Send Command
          </Typography>
          <Chip
            size="small"
            icon={<GpsFixedIcon />}
            label={protocolLabel}
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        </Stack>

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Device: <b>{modalDevice?.name}</b> • IMEI: <b>{modalDevice?.imei}</b> • Status:{" "}
          <b style={{ color: modalDevice?.status === "online" ? theme.palette.success.main : theme.palette.error.main }}>
            {safeString(modalDevice?.status).toUpperCase()}
          </b>
        </Typography>
      </Stack>

      <IconButton onClick={onClose} aria-label="close">
        <CloseIcon />
      </IconButton>
    </Stack>
  );

  const Alerts = () => (
    <Stack spacing={0.9} sx={{ mt: 1.25 }}>
      {templatesError && (
        <Alert severity="error" sx={{ py: 0.15, alignItems: "center", "& .MuiAlert-message": { fontSize: 13 } }}>
          {templatesError}
        </Alert>
      )}
      {errorMsg && (
        <Alert severity="error" sx={{ py: 0.15, alignItems: "center", "& .MuiAlert-message": { fontSize: 13 } }}>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ py: 0.15, alignItems: "center", "& .MuiAlert-message": { fontSize: 13 } }}>
          {successMsg}
        </Alert>
      )}
      {!!commandStatus && (
        <Alert
          severity={statusColor(commandStatus)}
          sx={{ py: 0.1, alignItems: "center", "& .MuiAlert-message": { fontSize: 12.5 } }}
        >
          Status: <b>{safeString(commandStatus).toUpperCase()}</b>
        </Alert>
      )}
    </Stack>
  );

  const StepChips = () => {
    const steps = ["select", "params", "confirm", "review", "transport"];
    const labels = {
      select: "Select",
      params: "Parameters",
      confirm: "Confirm",
      review: "Review",
      transport: "Send",
    };

    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 2 }}>
        {steps.map((s) => (
          <Chip
            key={s}
            label={labels[s]}
            size="small"
            color={step === s ? "primary" : "default"}
            variant={step === s ? "filled" : "outlined"}
          />
        ))}
      </Stack>
    );
  };

  const SelectStep = () => (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Available Commands
        </Typography>

        <Button
          size="small"
          variant="outlined"
          onClick={fetchTemplates}
          disabled={templatesLoading}
          startIcon={templatesLoading ? <CircularProgress size={16} /> : <SettingsSuggestIcon />}
        >
          Refresh
        </Button>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: 1.2,
          borderRadius: 2.5,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Commands are loaded from backend templates. Pick a command and send quickly.
        </Typography>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 1.3,
          borderRadius: 2.5,
          borderColor: alpha(theme.palette.warning.main, 0.35),
          bgcolor: alpha(theme.palette.warning.main, 0.06),
        }}
      >
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <PowerSettingsNewIcon fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              Quick Engine Control
            </Typography>
            <Chip
              size="small"
              label={`ACC: ${modalDevice?.liveData?.accStatus ? "ON" : "OFF"}`}
              color={modalDevice?.liveData?.accStatus ? "success" : "default"}
              variant={modalDevice?.liveData?.accStatus ? "filled" : "outlined"}
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              color="error"
              startIcon={<StopCircleIcon />}
              disabled={!engineOffTemplate}
              onClick={() => engineOffTemplate && onSelectTemplate(engineOffTemplate)}
              sx={{ flex: 1, fontWeight: 800 }}
            >
              Engine OFF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              disabled={!engineOnTemplate}
              onClick={() => engineOnTemplate && onSelectTemplate(engineOnTemplate)}
              sx={{ flex: 1, fontWeight: 800 }}
            >
              Engine ON
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {templatesLoading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            Loading commands…
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.2}>
          {categories.map((cat) => (
            <Accordion key={cat.category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800 }}>{cat.category}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {cat.commands.length} commands
                  </Typography>
                </Stack>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={1}>
                  {cat.commands.map((tpl) => {
                    const danger = !!tpl.dangerous;
                    const needsPwd = !!tpl.requiresPassword;

                    return (
                      <Paper
                        key={tpl._id || tpl.type}
                        variant="outlined"
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          display: "flex",
                          gap: 1.5,
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          borderColor: danger ? alpha(theme.palette.error.main, 0.35) : undefined,
                        }}
                      >
                        <Stack spacing={0.3} sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 800 }}>
                              {tpl.name || tpl.type}
                            </Typography>

                            {danger && (
                              <Chip
                                size="small"
                                color="error"
                                icon={<WarningAmberIcon />}
                                label="Dangerous"
                              />
                            )}

                            {tpl.confirmationRequired && (
                              <Chip size="small" variant="outlined" label="Confirmation" />
                            )}

                            {needsPwd && (
                              <Chip size="small" variant="outlined" icon={<LockIcon />} label="Password" />
                            )}
                          </Stack>

                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {tpl.description || "—"}
                          </Typography>

                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Type: <b>{tpl.type}</b> • GPS: <b>{modalDevice?.deviceType || resolveDeviceProtocolHint(modalDevice) || "GT06"}</b>
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Tooltip title="Open command">
                            <Button
                              variant="contained"
                              onClick={() => onSelectTemplate(tpl)}
                              size="small"
                            >
                              Select
                            </Button>
                          </Tooltip>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </Stack>
  );

  const ParamsStep = () => {
    if (!selectedTpl) return null;

    const defs = Array.isArray(selectedTpl.parameters) ? selectedTpl.parameters : [];

    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.4,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.secondary.main, 0.05),
          }}
        >
          <Stack spacing={0.6}>
            <Typography sx={{ fontWeight: 900 }}>{selectedTpl.name || selectedTpl.type}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {selectedTpl.description || "No description provided."}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.6 }}>
              <Chip size="small" label={`Category: ${selectedTpl.category || "Other"}`} />
              {selectedTpl.dangerous && <Chip size="small" color="error" label="Dangerous" />}
              {selectedTpl.confirmationRequired && <Chip size="small" variant="outlined" label="Confirmation needed" />}
              {shouldShowPasswordField && (
                <Chip size="small" variant="outlined" icon={<LockIcon />} label="Password required" />
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* Dynamic parameters */}
        {defs.length > 0 && (
          <Stack spacing={1.4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Parameters
            </Typography>

            <Stack spacing={1.2}>
              {defs.map((p) => {
                if (!SHOW_PASSWORD_SECTION && isPasswordParamDef(p)) return null;

                const value = parameters?.[p.name] ?? "";
                const isNumber = p.type === "number";

                return (
                  <TextField
                    key={p.name}
                    label={`${p.name}${p.required ? " *" : ""}`}
                    value={value}
                    onChange={(e) => setParameters((prev) => ({ ...prev, [p.name]: e.target.value }))}
                    type={isNumber ? "number" : "text"}
                    inputProps={{
                      min: typeof p.min === "number" ? p.min : undefined,
                      max: typeof p.max === "number" ? p.max : undefined,
                    }}
                    helperText={
                      [
                        p.description,
                        p.unit ? `Unit: ${p.unit}` : null,
                        p.example ? `Example: ${p.example}` : null,
                        typeof p.min === "number" ? `Min: ${p.min}` : null,
                        typeof p.max === "number" ? `Max: ${p.max}` : null,
                      ]
                        .filter(Boolean)
                        .join(" • ")
                    }
                    fullWidth
                  />
                );
              })}
            </Stack>
          </Stack>
        )}

        {/* Password (if required) */}
        {shouldShowPasswordField && (
          <TextField
            label="Login Password *"
            value={devicePassword}
            onChange={(e) => setDevicePassword(e.target.value)}
            type="password"
            autoComplete="new-password"
            inputProps={{ autoCapitalize: "off", autoCorrect: "off", spellCheck: false }}
            helperText={
              allowEmptyPassword
                ? "Optional. If your tracker uses a command password, enter it here."
                : "Enter the device command/relay password if your tracker requires one. Common default is 123456."
            }
            fullWidth
          />
        )}

        {/* Priority */}
        <FormControl fullWidth>
          <InputLabel>Priority</InputLabel>
          <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value)}>
            {PRIORITIES.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Raw command field if template type is custom_raw */}
        {selectedTpl.type === "custom_raw" && (
          <TextField
            label="Raw Command *"
            value={rawCommandValue}
            onChange={(e) => setParameters((prev) => ({ ...prev, rawCommand: e.target.value }))}
            placeholder="e.g., RELAY,1,123456#  OR  SERVER,1,domain.com,5023,123456#"
            helperText="Raw commands are not auto-built by backend. Include password/# if needed."
            fullWidth
            multiline
            minRows={2}
          />
        )}
      </Stack>
    );
  };

  const ConfirmStep = () => {
    if (!selectedTpl) return null;

    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Alert severity="warning" icon={<WarningAmberIcon />}>
          This command is marked as <b>dangerous</b>. Please confirm before continuing.
        </Alert>

        <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2 }}>
          <Stack spacing={0.6}>
            <Typography sx={{ fontWeight: 900 }}>{selectedTpl.name || selectedTpl.type}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {selectedTpl.description || "—"}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">
              Type <b>CONFIRM</b> to proceed.
            </Typography>
            <TextField
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
                           placeholder="CONFIRM"
              fullWidth
            />
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const ReviewStep = () => {
    if (!selectedTpl) return null;

    const defs = Array.isArray(selectedTpl.parameters) ? selectedTpl.parameters : [];
    const isRaw = selectedTpl.type === "custom_raw";

    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.6,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 900 }} variant="subtitle1">
                Review
              </Typography>
              <Chip size="small" label={`Priority: ${priority}`} />
              {selectedTpl.dangerous && <Chip size="small" color="error" label="Dangerous" />}
              {selectedTpl.confirmationRequired && (
                <Chip size="small" variant="outlined" label="Confirmation required" />
              )}
              {SHOW_PASSWORD_SECTION && selectedTpl.requiresPassword && (
                <Chip size="small" variant="outlined" icon={<LockIcon />} label="Password" />
              )}
            </Stack>

            <Divider />

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Device:</b> {modalDevice?.name} • <b>IMEI:</b> {modalDevice?.imei} • <b>Status:</b>{" "}
              {safeString(modalDevice?.status).toUpperCase()}
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>GPS Protocol:</b> {protocolLabel}
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Command:</b> {selectedTpl.name || selectedTpl.type} • <b>Type:</b>{" "}
              {selectedTpl.type}
            </Typography>

            <Divider />

            {!isRaw ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Parameters (sent to backend)
                </Typography>

                {defs.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No parameters required.
                  </Typography>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      bgcolor: "#fff",
                      overflow: "auto",
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: 12 }}>
                      {JSON.stringify(parameters, null, 2)}
                    </pre>
                  </Paper>
                )}

                {shouldShowPasswordField && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Login Password: <b>{devicePassword ? "••••••" : "Not set"}</b>
                  </Typography>
                )}

                <Alert severity="info" icon={<WifiTetheringIcon />}>
                  Backend will build the final protocol packet from this template and push it to the device over TCP.
                </Alert>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Raw Command (sent as-is)
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: "#fff",
                    overflow: "auto",
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      m: 0,
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {rawCommandValue || "—"}
                  </Typography>
                </Paper>

                <Alert severity="warning" icon={<WarningAmberIcon />}>
                  Raw command backend auto-build nahi karta. Agar device password/# chahiye, raw command me include karo.
                </Alert>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const TransportStep = () => {
    if (!selectedTpl) return null;

    const isRaw = selectedTpl.type === "custom_raw";

    const canSend =
      !isSending &&
      (!needsConfirm || validateConfirm()) &&
      validateParams().ok &&
      (isRaw ? !!rawCommandValue?.trim() : true);

    const onSendGprs = async () => {
      setErrorMsg(null);
      const v = validateParams();
      if (!v.ok) {
        setErrorMsg(v.msg);
        return;
      }
      if (needsConfirm && !validateConfirm()) {
        setErrorMsg('Type "CONFIRM" to proceed.');
        return;
      }
      if (isRaw) {
        await sendRawCommand(rawCommandValue);
      } else {
        await sendTypedCommand();
      }
    };

    const onSendSms = () => {
      setErrorMsg(null);
      const v = validateParams();
      if (!v.ok) {
        setErrorMsg(v.msg);
        return;
      }
      if (needsConfirm && !validateConfirm()) {
        setErrorMsg('Type "CONFIRM" to proceed.');
        return;
      }

      // SMS: typed command ke liye backend finalCommand build nahi karega SMS me.
      // Isliye SMS mode: sirf Raw recommended.
      // Still allow, but show clear message.
      if (!isRaw) {
        setErrorMsg("SMS mode recommended only for Raw command (because backend builds typed command). Use GPRS for typed.");
        return;
      }

      prepareSms(rawCommandValue);
    };

    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.6,
            borderRadius: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
            Choose Delivery Method
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <b>GPRS:</b> Backend via socket (device online ho to instant, warna queue). <br />
            <b>SMS:</b> Phone SMS app se manual send (offline case me).
          </Typography>

          <Stack
            direction={fullScreen ? "column" : "row"}
            spacing={1.2}
            sx={{ pt: 0.5 }}
          >
            <Button
              onClick={onSendGprs}
              variant="contained"
              size="large"
              startIcon={isSending ? <CircularProgress size={18} /> : <SendIcon />}
              disabled={!canSend}
              sx={{ flex: 1 }}
            >
              {isSending ? "Sending…" : "Send via GPRS"}
            </Button>

            <Button
              onClick={onSendSms}
              variant="outlined"
              size="large"
              startIcon={<SmsIcon />}
              disabled={!canSend}
              sx={{ flex: 1 }}
            >
              Send via SMS
            </Button>
          </Stack>

          {!isRaw && (
            <Alert severity="info" icon={<WifiTetheringIcon />}>
              Typed commands ke liye best: <b>GPRS</b> (backend final GT06 command build karke send karta hai).
            </Alert>
          )}
        </Paper>
      </Stack>
    );
  };

  // ------------------------
  // Footer actions
  // ------------------------
  const goBack = () => {
    setErrorMsg(null);
    setSuccessMsg(null);

    if (step === "params") return setStep("select");
    if (step === "confirm") {
      const hasParamsLocal = !!(selectedTpl?.parameters?.length) || !!selectedTpl?.requiresPassword;
      return setStep(hasParamsLocal ? "params" : "select");
    }
    if (step === "review") {
      if (needsConfirm) return setStep("confirm");
      const hasParamsLocal = !!(selectedTpl?.parameters?.length) || !!selectedTpl?.requiresPassword;
      return setStep(hasParamsLocal ? "params" : "select");
    }
    if (step === "transport") return setStep("review");
    return setStep("select");
  };

  const canContinue = () => {
    if (step === "select") return !!selectedTpl; // normally select sets tpl; safe
    if (step === "params") return validateParams().ok;
    if (step === "confirm") return validateConfirm();
    return true;
  };

  const onContinue = () => {
    setErrorMsg(null);

    if (step === "select") {
      if (!selectedTpl) {
        setErrorMsg("Please select a command.");
        return;
      }
      const hasParamsLocal = !!(selectedTpl?.parameters?.length) || !!selectedTpl?.requiresPassword;
      if (hasParamsLocal) setStep("params");
      else if (needsConfirm) setStep("confirm");
      else setStep("review");
      return;
    }

    if (step === "params") {
      const v = validateParams();
      if (!v.ok) {
        setErrorMsg(v.msg);
        return;
      }
      if (needsConfirm) setStep("confirm");
      else setStep("review");
      return;
    }

    if (step === "confirm") {
      if (!validateConfirm()) {
        setErrorMsg('Type "CONFIRM" to proceed.');
        return;
      }
      setStep("review");
      return;
    }

    if (step === "review") {
      setStep("transport");
      return;
    }
  };

  // ------------------------
  // Render
  // ------------------------
    if (!modalDevice) return null;

const SimpleModeBody = () => {
  const defs = Array.isArray(selectedTpl?.parameters) ? selectedTpl.parameters : [];
  const rawCommandValue = safeString(parameters.rawCommand);

  return (
    <Stack spacing={isSimpleMode ? 1.5 : 2.25}>
        <Box>
          <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 0.75 }}>Object:</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 500, color: "#1f2937" }}>
            {modalDevice?.name || modalDevice?.vehicleName || modalDevice?.imei || "Unknown Device"}
          </Typography>
        </Box>

        {templatesLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <FormControl fullWidth>
            <InputLabel shrink htmlFor="simple-command-trigger">
              Command
            </InputLabel>
            <Box
              id="simple-command-trigger"
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              aria-expanded={simpleCommandMenuOpen ? "true" : "false"}
              onClick={() => {
                setSimpleCommandPickerOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSimpleCommandPickerOpen(true);
                }
              }}
              sx={{
                minHeight: { xs: 54, sm: 60 },
                mt: 1.1,
                px: 1.75,
                py: 1.55,
                borderRadius: 2.25,
                border: "1px solid rgba(148, 163, 184, 0.32)",
                backgroundColor: alpha(theme.palette.common.white, 0.96),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.25,
                cursor: "pointer",
                transition: "border-color 120ms ease, box-shadow 120ms ease",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.55),
                },
                "&:focus-visible": {
                  outline: "none",
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 600, color: "#111827" }}>
                  {selectedSimpleTemplate ? getSimpleCommandLabel(selectedSimpleTemplate) : "Select Command"}
                </Typography>
                {selectedTpl && safeString(selectedTpl?.description) && (
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.35 }} noWrap>
                    {selectedTpl.description}
                  </Typography>
                )}
              </Box>
              <ExpandMoreIcon
                sx={{
                  color: "text.secondary",
                  transform: simpleCommandMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 140ms ease",
                }}
              />
            </Box>
            {simpleCommandPickerOpen && (
              <>
                <Box
                  onClick={() => setSimpleCommandPickerOpen(false)}
                  sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1400,
                    backgroundColor: "rgba(15, 23, 42, 0.18)",
                  }}
                />
                <Paper
                  elevation={20}
                  role="dialog"
                  aria-modal="true"
                  sx={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1401,
                    width: "min(92vw, 380px)",
                    maxHeight: "min(70vh, 520px)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2.5,
                    border: "1px solid rgba(148, 163, 184, 0.28)",
                    backgroundColor: alpha(theme.palette.background.paper, 0.98),
                    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(226, 232, 240, 0.9)" }}
                  >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
                      Select Command
                    </Typography>
                    <IconButton size="small" onClick={() => setSimpleCommandPickerOpen(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Box
                    sx={{
                      p: 1.25,
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      overscrollBehavior: "contain",
                      WebkitOverflowScrolling: "touch",
                      maxHeight: "calc(min(70vh, 520px) - 68px)",
                    }}
                  >
                    <Stack spacing={0.75}>
                      {renderedSimpleTemplates.map((option) => {
                        const optionType = safeString(option?.type);
                        const isSelected =
                          safeString(selectedTpl?.type).toLowerCase() === optionType.toLowerCase();
                        return (
                          <Button
                            key={optionType}
                            variant={isSelected ? "contained" : "outlined"}
                            color={isSelected ? "primary" : "inherit"}
                            onClick={() => {
                              selectTemplate(option, { advance: false });
                              setSimpleCommandPickerOpen(false);
                            }}
                            sx={{
                              justifyContent: "flex-start",
                              textAlign: "left",
                              px: 1.5,
                              py: 1.25,
                              borderRadius: 1.75,
                              textTransform: "none",
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                {getSimpleCommandLabel(option)}
                              </Typography>
                              {safeString(option?.description) && (
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: isSelected
                                      ? "rgba(255,255,255,0.86)"
                                      : "text.secondary",
                                    mt: 0.35,
                                  }}
                                >
                                  {option.description}
                                </Typography>
                              )}
                            </Box>
                          </Button>
                        );
                      })}
                    </Stack>
                  </Box>
                </Paper>
              </>
            )}
          </FormControl>
        )}

        {!templatesLoading && simpleTemplates.length === 0 && (
          <Alert severity="warning">No commands available for this device.</Alert>
        )}

        {selectedTpl && safeString(selectedTpl?.description) && (
          <Typography variant="body2" sx={{ color: "text.secondary", mt: -0.75 }}>
            {selectedTpl.description}
          </Typography>
        )}

        {defs.length > 0 && (
          <Stack spacing={1.2}>
            {defs.map((p) => {
              if (!SHOW_PASSWORD_SECTION && isPasswordParamDef(p)) return null;
              const value = parameters?.[p.name] ?? "";
              const isNumber = p.type === "number";
              return (
                <TextField
                  key={p.name}
                  label={`${p.name}${p.required ? " *" : ""}`}
                  value={value}
                  onChange={(e) => setParameters((prev) => ({ ...prev, [p.name]: e.target.value }))}
                  type={isNumber ? "number" : "text"}
                  inputProps={{
                    min: typeof p.min === "number" ? p.min : undefined,
                    max: typeof p.max === "number" ? p.max : undefined,
                  }}
                  helperText={p.description || p.example ? `Example: ${p.example || ""}` : ""}
                  fullWidth
                />
              );
            })}
          </Stack>
        )}

        {selectedTpl?.type === "custom_raw" && (
          <TextField
            label="Raw Command *"
            value={rawCommandValue}
            onChange={(e) => setParameters((prev) => ({ ...prev, rawCommand: e.target.value }))}
            placeholder="RELAY,1#"
            multiline
            minRows={2}
            fullWidth
          />
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      disableEnforceFocus={isSimpleMode}
      disableRestoreFocus={isSimpleMode}
      fullScreen={fullScreen}
      maxWidth={isSimpleMode ? "xs" : "md"}
      fullWidth
      keepMounted
      TransitionComponent={Fade}
      transitionDuration={{ enter: 180, exit: 140 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.38)",
            backdropFilter: "blur(2px)",
          },
        },
      }}
PaperProps={{
  sx: {
    borderRadius: fullScreen ? 0 : 2.5,
    overflow: "hidden",
    // width: isSimpleMode ? "calc(100vw - 24px)" : "calc(100vw - 28px)",
    maxWidth: isSimpleMode ? { xs: 280, sm: 320 } : 920,
    maxHeight: isSimpleMode ? { xs: "auto", sm: "min(75vh, 550px)" } : fullScreen ? "100%" : "min(70vh, 760px)",
    mx: "auto",
    boxShadow: fullScreen ? "none" : "0 24px 70px rgba(15, 23, 42, 0.18)",
    border: fullScreen ? "none" : "1px solid rgba(148, 163, 184, 0.16)",
    backgroundImage: "none",
    animation: fullScreen ? "none" : "commandModalScaleIn 180ms ease-out",
    transformOrigin: "center center",
    "@keyframes commandModalScaleIn": {
      "0%": {
        opacity: 0,
        transform: "scale(0.975)",
      },
      "100%": {
        opacity: 1,
        transform: "scale(1)",
      },
    },
  },
}}
    >
      <DialogTitle
        sx={{
          px: isSimpleMode ? { xs: 2, sm: 2.5 } : 2.5,
          pt: isSimpleMode ? { xs: 1.75, sm: 2 } : 2,
          pb: isSimpleMode ? 0.75 : 1.25,
        }}
      >
        {isSimpleMode ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Typography
              sx={{
                fontSize: { xs: 19, sm: 21 },
                lineHeight: 1.2,
                fontWeight: 800,
                color: "#111827",
              }}
            >
              Send GPRS Command
            </Typography>
            <IconButton
              onClick={onClose}
              aria-label="close"
              size="small"
              sx={{
                color: "text.secondary",
                border: "1px solid rgba(148, 163, 184, 0.2)",
                bgcolor: alpha(theme.palette.common.white, 0.88),
                "&:hover": {
                  bgcolor: alpha(theme.palette.common.black, 0.04),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        ) : (
          <>
            <Header />
            <StepChips />
          </>
        )}
        <Alerts />
      </DialogTitle>

    <DialogContent
  dividers
  sx={{
    px: isSimpleMode ? { xs: 1.5, sm: 2 } : 2.5,
    py: isSimpleMode ? 1.2 : 2,
          bgcolor: isSimpleMode ? "#fff" : "background.default",
          "&.MuiDialogContent-dividers": {
            borderTopColor: "rgba(226, 232, 240, 0.9)",
            borderBottomColor: "rgba(226, 232, 240, 0.9)",
          },
        }}
      >
        {isSimpleMode ? (
          <SimpleModeBody />
        ) : (
          <>
            {step === "select" && <SelectStep />}
            {step === "params" && <ParamsStep />}
            {step === "confirm" && <ConfirmStep />}
            {step === "review" && <ReviewStep />}
            {step === "transport" && <TransportStep />}
          </>
        )}
      </DialogContent>

      {isSimpleMode ? (
        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 1.75 },
            justifyContent: "flex-start",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={async () => {
              const v = validateParams();
              if (!v.ok) {
                setErrorMsg(v.msg);
                return;
              }
              if (selectedTpl?.type === "custom_raw") {
                await sendRawCommand(safeString(parameters.rawCommand));
                return;
              }
              await sendTypedCommand();
            }}
            variant="contained"
            disabled={isSending || !selectedTpl}
            sx={{
              minWidth: 104,
              px: 2,
              borderRadius: 2,
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            {isSending ? "Sending..." : "Send"}
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            color="inherit"
            sx={{
              minWidth: 96,
              px: 2,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      ) : (
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button onClick={goBack} variant="outlined">
              Back
            </Button>
            <Button onClick={onClose} color="error" variant="text">
              Close
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {step !== "transport" && (
              <Button
                onClick={onContinue}
                variant="contained"
                disabled={!canContinue()}
              >
                Continue
              </Button>
            )}
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default React.memo(SendCommandModal);
